import React, { useEffect, useState } from 'react';
import {
  errorMessage,
  onboardingStepId,
  pagination,
  speciesSubsteps,
  toastMessage,
} from '@helpers/Constants';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch } from '@fortawesome/free-solid-svg-icons';
import { ApiEndpoints, FrontendRoutes } from '@helpers/Constants/Navigation';
import { Axios, getErrorMessage } from '@helpers/ApiService';
import { useToast } from '@contexts/Toast';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  MultiSelectionProps,
  SpeciesColor,
  FetchColorResponse,
  ApiColorResponse,
} from '@helpers/Models';
import { faTrashCan } from '@fortawesome/free-regular-svg-icons';
import { Breadcrumbs } from '@components/Breadcrumbs';
import { ButcherHeader } from '@components/ButcherHeader';
import { Assets } from '@helpers/Constants/Assets';
import Pagination from '@components/Pagination';
import { UseConfirmationToast } from '@contexts/ConfirmationToast';
import { isModified } from '@services/isModified';
import { OnBoardSettings } from '@components/OnBoardSettings';
import { useLocalStorage } from '@hooks/useLocalStorage';
import { useUnsavedChangesPrompt } from '@hooks/useNavigationBlocker';

interface FullColor extends SpeciesColor {
  deletedAt: string | null;
}

export const Color: React.FC = () => {
  const navigate = useNavigate();
  const { updateOnboardingStep } = useLocalStorage();
  const { showToastMessage } = useToast();
  const { showConfirmationToast } = UseConfirmationToast();
  const location = useLocation();
  const fromSettings = location.state?.fromSettings;

  const [colors, setColors] = useState<SpeciesColor[]>([]);
  const [allColorsGlobal, setAllColorsGlobal] = useState<FullColor[]>([]);
  const [initialList, setInitialList] = useState<SpeciesColor[]>([]);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [allSelected, setAllSelected] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(pagination.itemsPerPage);
  const [totalPages, setTotalPages] = useState(1);
  const [searchKey, setSearchKey] = useState('');
  const [selectedRecords, setSelectedRecords] = useState<MultiSelectionProps[]>([]);
  const [onDeletion, setOnDeletion] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // ---- Derive unsaved changes ----
  useEffect(() => {
    setHasUnsavedChanges(isModified(initialList, colors));
  }, [colors, initialList]);

  useUnsavedChangesPrompt(hasUnsavedChanges, undefined, 'Leave Without Saving');

  // ---- Refresh the displayed list from global data ----
  const refreshDisplay = () => {
    const active = allColorsGlobal.filter((c) => c.deletedAt === null);
    const filtered = active.filter((c) =>
      c.color.toLowerCase().includes(searchKey.trim().toLowerCase())
    );
    const total = filtered.length;
    const totalPagesCalc = Math.ceil(total / itemsPerPage) || 1;
    const safePage = Math.min(currentPage, totalPagesCalc);
    const start = (safePage - 1) * itemsPerPage;
    const paginated = filtered.slice(start, start + itemsPerPage);

    setColors(paginated);
    setInitialList(paginated);
    setTotalPages(totalPagesCalc);
    if (safePage !== currentPage) {
      setCurrentPage(safePage);
    }
  };

  // ---- Fetch all colors from the server (global list) ----
  const fetchAllColors = async () => {
    try {
      const response = await Axios.post(ApiEndpoints.FETCH_COLORS, {
        page: 1,
        count: 1000,
        searchKey: '',
      });

      const fullColors: FullColor[] = response.data.data.colors.map(
        (color: FetchColorResponse & { deletedAt?: string | null }) => ({
          id: color.id,
          color: color.color,
          availableFlag: Boolean(color.availableFlag),
          deletedAt: color.deletedAt || null,
        })
      );
      setAllColorsGlobal(fullColors);
    } catch (error) {
      showToastMessage(errorMessage.FETCH_ERROR, 'error');
    }
  };

  // ---- Initial load ----
  useEffect(() => {
    fetchAllColors();
  }, []);

  // ---- Refresh when global data or filters change ----
  useEffect(() => {
    refreshDisplay();
  }, [allColorsGlobal, searchKey, currentPage, itemsPerPage]);

  // ---- Build a lookup map of deleted colors for fast restore detection ----
  const getDeletedLookup = (): Map<string, number> => {
    const map = new Map<string, number>();
    for (const color of allColorsGlobal) {
      if (color.deletedAt !== null) {
        const key = color.color.trim().toLowerCase();
        // If multiple deleted colors with same name (shouldn't happen), keep the first
        const deletedId = color.id;
        if (!map.has(key) && deletedId !== undefined) {
          map.set(key, deletedId);
        }
      }
    }
    return map;
  };

  // ---- Validation helpers ----
  const validateField = (value: string, index?: number): string => {
    if (!value.trim()) {
      return errorMessage.FIELD_REQUIRED;
    }

    // Local duplicate check
    const isLocalDuplicate = colors.some((item, i) => {
      if (index !== undefined && i === index) return false;
      return item.color.trim().toLowerCase() === value.trim().toLowerCase();
    });
    if (isLocalDuplicate) {
      return errorMessage.COLOR_EXISTS;
    }

    // Global duplicate check (only against active colors)
    const isGlobalDuplicate = allColorsGlobal.some(
      (c) =>
        c.deletedAt === null &&
        c.color.trim().toLowerCase() === value.trim().toLowerCase() &&
        (index === undefined ||
          colors[index]?.id === undefined ||
          c.id !== colors[index]?.id)
    );
    if (isGlobalDuplicate) {
      return errorMessage.DUPLICATE_ERROR;
    }

    return '';
  };

  const validateColorsList = (colorList: SpeciesColor[]): { [key: string]: string } => {
    const newErrors: { [key: string]: string } = {};
    colorList.forEach((color, idx) => {
      const error = validateField(color.color, idx);
      if (error) newErrors[`color-${idx}`] = error;
    });
    return newErrors;
  };

  const validateAll = (): boolean => {
    const localErrors = validateColorsList(colors);
    setErrors(localErrors);
    return Object.keys(localErrors).length > 0;
  };

  // ---- Handlers for inline editing ----
  const handleColorFieldChange = (index: number, value: string): void => {
    const updatedColors = [...colors];
    updatedColors[index] = { ...updatedColors[index], color: value };
    setColors(updatedColors);

    const error = validateField(value, index);
    setErrors((prev) => ({ ...prev, [`color-${index}`]: error }));
  };

  const handleAvailableFlagChange = (index: number, checked: boolean): void => {
    const updatedColors = [...colors];
    updatedColors[index] = { ...updatedColors[index], availableFlag: checked };
    setColors(updatedColors);
  };

  const handleSelectAll = (): void => {
    const dataSelected = !allSelected;
    setAllSelected(dataSelected);
    const updatedColors = colors.map((color) => ({
      ...color,
      availableFlag: dataSelected,
    }));
    setColors(updatedColors);
  };

  // ---- Add new empty color ----
  const handleAddMore = (): void => {
    const hasEmptyColor = colors.some((c) => !c.color || !c.color.trim());
    if (hasEmptyColor) {
      showToastMessage(
        'Please complete the color entry before creating a new one.',
        'error'
      );
      return;
    }

    const newColor: SpeciesColor = { color: '', availableFlag: true };
    setColors([...colors, newColor]);
    setTimeout(() => {
      const scrollArea = document.getElementById('color-table-scroll-area');
      if (scrollArea) {
        scrollArea.scrollTop = scrollArea.scrollHeight;
      }
    }, 100);
  };

  // ---- Delete ----
  const performDelete = async (id: number | undefined, index: number) => {
    try {
      if (id !== undefined) {
        await Axios.delete(ApiEndpoints.DELETE_COLORS, { data: { ids: [id] } });
        await fetchAllColors();
        setErrors({});
        showToastMessage('Color deleted successfully', 'success');
      } else {
        const newColors = colors.filter((_, i) => i !== index);
        setColors(newColors);
        setInitialList(newColors);
        setErrors(validateColorsList(newColors));
        showToastMessage('Color removed', 'success');
      }
    } catch (error) {
      showToastMessage(errorMessage.DELETE_FAILED, 'error');
    }
  };

  const handleDelete = (id: number | undefined, index: number) => {
    showConfirmationToast(
      'Are you sure you want to delete this item?',
      'Delete Confirmation',
      'delete',
      () => performDelete(id, index),
      'Delete',
      undefined,
      'Cancel'
    );
  };

  // ---- Multiple delete ----
  const handleMultipleDeleteSelection = (id: number | undefined, index: number) => {
    setSelectedRecords((prev) => {
      const exists = prev.some((record) =>
        id !== undefined ? record.id === id : record.index === index
      );
      return exists
        ? prev.filter((record) =>
          id !== undefined ? record.id !== id : record.index !== index
        )
        : [...prev, { id, index }];
    });
  };

  const handleMultipleDelete = async () => {
    try {
      const dataWithId = selectedRecords.filter((r) => r.id !== undefined);
      const dataWithIndex = selectedRecords.map((r) => r.index);

      if (dataWithId.length > 0) {
        const payload = dataWithId.map((r) => r.id);
        await Axios.delete(ApiEndpoints.DELETE_COLORS, { data: { ids: payload } });
        await fetchAllColors();
      } else if (dataWithIndex.length > 0) {
        const newColors = colors.filter((_, i) => !dataWithIndex.includes(i));
        setColors(newColors);
        setInitialList(newColors);
        setErrors(validateColorsList(newColors));
      }

      setSelectedRecords([]);
      if (dataWithId.length > 0) setErrors({});
      showToastMessage('Selected colors removed', 'success');
    } catch (error) {
      showToastMessage(errorMessage.DELETE_FAILED, 'error');
    }
  };

  // ---- Save ----
  const handleSubmit = async (event: React.FormEvent, nextPage: boolean) => {
    event.preventDefault();

    if (validateAll()) {
      showToastMessage('Please fix all errors before proceeding.', 'error');
      return;
    }

    if (colors.length === 0) {
      showToastMessage(errorMessage.REQUIRED_ERROR, 'error');
      return;
    }

    const navigateToNextPage = () => {
      updateOnboardingStep(onboardingStepId.COLOR);
      navigate(FrontendRoutes.SPECIES_AND_SERVICE, { state: fromSettings ? { fromSettings } : undefined });
    };

    if (nextPage) {
      navigateToNextPage();
      return;
    }

    if (!isModified(initialList, colors)) {
      showToastMessage('No changes to save.', 'success');
      return;
    }

    // Build a lookup map of deleted colors (normalized name → id)
    const deletedLookup = getDeletedLookup();

    // Identify colors to restore and the ones to keep
    const restoreIds: number[] = [];
    const colorsToKeep: SpeciesColor[] = [];

    for (const color of colors) {
      if (color.id === undefined) {
        const normalizedName = color.color.trim().toLowerCase();
        const deletedId = deletedLookup.get(normalizedName);
        if (deletedId !== undefined) {
          restoreIds.push(deletedId);
          continue; // this color will be restored, not upserted
        }
      }
      colorsToKeep.push(color);
    }

    // If we have colors to restore, call the restore API and finish
    if (restoreIds.length > 0) {
      try {
        await Axios.post(ApiEndpoints.RESTORE_COLORS, { ids: restoreIds });
        showToastMessage('Color restored successfully', 'success');
        await fetchAllColors();
        return;
      } catch (error) {
        showToastMessage('Failed to restore color(s).', 'error');
        return;
      }
    }

    // Otherwise, proceed with upsert for the remaining colors
    const upsertPayload = colorsToKeep.map((color) => ({
      ...(color.id && { id: color.id }),
      color: color.color,
      availableFlag: color.availableFlag ? 1 : 0,
    }));

    try {
      const response = await Axios.post(ApiEndpoints.UPSERT_COLORS, {
        colors: upsertPayload,
      });

      if (response.data?.data) {
        const updatedColors = response.data.data.map((item: ApiColorResponse) => ({
          id: item.id,
          color: item.color,
          availableFlag: Boolean(item.availableFlag),
        }));

        const newCount = colors.filter((c) => !c.id).length;
        let updateCount = 0,
          deleteCount = 0;
        const updatedMap = new Map<number, SpeciesColor>();
        updatedColors.forEach((c: SpeciesColor) => {
          if (c.id !== undefined) {
            updatedMap.set(c.id, c);
          }
        });

        for (const item of initialList) {
          if (item.id !== undefined && updatedMap.has(item.id)) {
            const updatedItem = updatedMap.get(item.id);
            if (!updatedItem) continue;

            const initWithoutId = {
              color: item.color,
              availableFlag: item.availableFlag,
            };
            const updatedWithoutId = {
              color: updatedItem.color,
              availableFlag: updatedItem.availableFlag,
            };
            if (JSON.stringify(initWithoutId) !== JSON.stringify(updatedWithoutId)) {
              updateCount++;
            }
          }
          if (item.id !== undefined && !updatedMap.has(item.id)) {
            deleteCount++;
          }
        }

        const ops = [];
        if (newCount) ops.push(`${newCount} Color${newCount > 1 ? 's' : ''} added`);
        if (updateCount) ops.push(`${updateCount} Color${updateCount > 1 ? 's' : ''} updated`);
        if (deleteCount) ops.push(`${deleteCount} Color${deleteCount > 1 ? 's' : ''} deleted`);
        let msg = 'No changes made to colors';
        if (ops.length === 1) msg = `${ops[0]} successfully`;
        else if (ops.length === 2) msg = `${ops[0]} and ${ops[1]} successfully`;
        else if (ops.length === 3) msg = `${ops[0]}, ${ops[1]}, and ${ops[2]} successfully`;
        showToastMessage(msg, 'success');
      } else {
        showToastMessage(response.data.message, 'success');
      }

      await fetchAllColors();
    } catch (error) {
      showToastMessage(getErrorMessage(error), 'error');
    }
  };

  // ---- Search ----
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchKey(e.target.value);
  };

  const hasErrors = () => Object.values(errors).some((err) => err !== '');

  // ---- Render ----
  const renderContent = (
    <>
      <div>
        <Breadcrumbs
          title="Configure Settings"
          activeMainNav="Species"
          activeStep={3}
          subNavSteps={speciesSubsteps}
          fromSettings={!!fromSettings}
          forStoreSetting={false}
        />
      </div>
      <div className="mt-3 rounded-lg" style={{ height: 'calc(100vh - 220px)' }}>
        <div className="flex justify-between items-center mb-6">
          <h1 className="font-semibold text-xl px-5 mt-3">Color</h1>
          <div className="flex items-center gap-2">
            <div className="relative">
              <input
                type="text"
                placeholder="Search..."
                onChange={handleSearchChange}
                value={searchKey}
                className="border p-2 pl-3 pr-10 w-80 accent-primary focus:outline-primary border-gray-300 rounded"
              />
              <FontAwesomeIcon
                icon={faSearch}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500"
              />
            </div>
          </div>
        </div>
        <div
          className="flex-1 overflow-y-auto mb-6 relative shadow-lg h-[calc(100vh-480px)]"
          id="color-table-scroll-area"
        >
          <table className="w-full table-auto">
            <thead className="bg-[#F0F0F0] sticky top-0 z-10">
              <tr className="border-b">
                <th className="font-medium px-4 py-2 w-[60%]">
                  <div className="flex items-center gap-1 text-[16px] font-semibold text-[#1C1B1F]">
                    <span>Color</span>
                    <img src={Assets.SORT_ICON} alt="Sort" className="h-3 w-3" />
                  </div>
                </th>
                <th className="text-center font-semibold text-[#1C1B1F] px-2 py-2 w-[20%]">
                  Available{' '}
                  <input
                    onChange={handleSelectAll}
                    type="checkbox"
                    className="ml-2 form-checkbox h-4 w-4 accent-primary focus:ring-primary text-primary rounded-full"
                  />
                </th>
                <th className="text-end px-2 py-2 w-[10%]"></th>
              </tr>
            </thead>
            <tbody>
              {colors.length > 0 ? (
                colors.map((color, index) => (
                  <tr
                    key={color.id ?? `new-${index}`}
                    className={onDeletion ? 'bg-[#F6F5F5]' : ''}
                  >
                    <td className="px-4 py-2 w-[60%] align-top">
                      <div className="flex items-start gap-3">
                        {onDeletion && (
                          <input
                            type="checkbox"
                            checked={selectedRecords.some((record) =>
                              color.id !== undefined
                                ? record.id === color.id
                                : record.index === index
                            )}
                            onChange={() =>
                              handleMultipleDeleteSelection(color.id, index)
                            }
                            className="h-4 w-4 accent-primary focus:ring-primary border-gray-300 rounded mt-1"
                          />
                        )}
                        <div className="w-full max-w-xs">
                          <input
                            type="text"
                            placeholder="Color name"
                            value={color.color}
                            onChange={(e) =>
                              handleColorFieldChange(index, e.target.value)
                            }
                            disabled={onDeletion}
                            className={`text-[14px] font-medium text-black pb-1 focus:outline-none focus:border-primary border-b-2 w-full ${errors[`color-${index}`]
                                ? 'border-red-500'
                                : 'border-[#1a1a1a]'
                              }`}
                          />
                          {errors[`color-${index}`] && (
                            <p className="text-red-500 text-sm mt-1">
                              {errors[`color-${index}`]}
                            </p>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-2 w-[20%] text-center">
                      <input
                        type="checkbox"
                        disabled={onDeletion}
                        checked={color.availableFlag}
                        onChange={(e) =>
                          handleAvailableFlagChange(index, e.target.checked)
                        }
                        className="h-4 w-4 accent-primary focus:ring-primary border-gray-300 rounded"
                      />
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex justify-end gap-4">
                        <button
                          type="button"
                          onClick={() => handleDelete(color.id, index)}
                          disabled={onDeletion}
                          className={onDeletion ? 'opacity-50 cursor-not-allowed' : ''}
                        >
                          <FontAwesomeIcon icon={faTrashCan} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={3} className="text-center p-4">
                    No data available
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        {errors.form && (
          <p className="text-red-500 text-[15px] mt-2 px-4">
            {errorMessage.EMPTY_FORM_SUBMISSION}
          </p>
        )}
        <div>
          <Pagination
            totalPages={totalPages}
            currentPage={currentPage}
            itemsPerPage={itemsPerPage}
            onPageChange={setCurrentPage}
            onItemsPerPageChange={setItemsPerPage}
          />
        </div>
      </div>
      <div className="sticky bottom-0 bg-white mt-3 border-t border-gray-100">
        {onDeletion ? (
          <div className="flex w-full">
            <button
              type="button"
              onClick={() => setOnDeletion(false)}
              className="w-[50%] px-6 py-1 border border-primary rounded-sm text-sm font-medium text-primary bg-white hover:bg-primary hover:text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
            >
              Cancel
            </button>
            <button
              type="submit"
              onClick={() =>
                showConfirmationToast(
                  toastMessage.delteConfirmation,
                  'Remove',
                  'delete',
                  handleMultipleDelete,
                  'Remove all'
                )
              }
              className="w-[50%] px-6 py-1 border border-red-500 rounded-sm text-sm font-medium text-red-500 bg-white hover:text-red-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 flex items-center justify-center gap-2"
            >
              <FontAwesomeIcon icon={faTrashCan} />
              <span>Remove All</span>
            </button>
          </div>
        ) : (
          <div className="w-full py-6">
            <div className="flex flex-col 2xl:flex-row 2xl:items-center 2xl:justify-end gap-4 w-full">
              <button
                onClick={handleAddMore}
                type="button"
                className="w-full 2xl:w-[150px] h-8 px-6 flex items-center justify-center space-x-2 border border-transparent rounded-md text-sm font-medium text-white bg-primary focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary transition-colors duration-200"
              >
                <span>+ Add More</span>
              </button>
              <div className="w-full 2xl:w-auto flex items-center justify-between 2xl:justify-end gap-4">
                <button
                  type="button"
                  onClick={(e) => handleSubmit(e, false)}
                  disabled={!hasUnsavedChanges}
                  className={`w-1/2 2xl:w-[130px] h-8 px-6 border rounded-md text-sm font-medium flex items-center justify-center transition-all duration-200 ${hasUnsavedChanges
                      ? 'bg-white text-primary border-primary hover:bg-primary hover:text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary'
                      : 'bg-white text-[#00483480] cursor-not-allowed border-[#00483480] pointer-events-auto'
                    }`}
                >
                  Save
                </button>
                <button
                  type="button"
                  onClick={(e) => handleSubmit(e, true)}
                  disabled={hasUnsavedChanges || colors.length === 0 || hasErrors()}
                  className={`w-1/2 2xl:w-[150px] h-8 px-6 border rounded-md text-sm font-medium flex items-center justify-center space-x-2 transition-all duration-200 ${hasUnsavedChanges || colors.length === 0 || hasErrors()
                      ? 'bg-gray-300 text-gray-500 cursor-not-allowed border-gray-300'
                      : 'bg-primary text-white hover:bg-dark-primary border-transparent focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary'
                    }`}
                >
                  <span>Next</span>
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );

  return (
    <>
      {!fromSettings ? (
        <div className="w-screen h-screen overflow-auto flex justify-center items-start">
          <div className="w-11/12 p-4">
            <ButcherHeader isOnBoarding={true} />
            {renderContent}
          </div>
        </div>
      ) : (
        <OnBoardSettings renderContent={renderContent} speciesSetup={true} />
      )}
    </>
  );
};